import {Directive, Inject, Injectable, Input} from '@angular/core';
import {DateAdapter} from '@angular/material/core';
import {
  DateRange,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDateRangeSelectionStrategy
} from '@angular/material/datepicker';

/**
 * mat-date-range-picker の選択可能な範囲を制限するDirective
 * [maxRange]=5 と指定したら視点の日付から5日後までだけ選択可能となる
 * https://stackoverflow.com/a/64527254
 */
@Injectable()
export class MaxRangeSelectionStrategy<D>
implements MatDateRangeSelectionStrategy<D> {
  start = '';
  public delta = 7;
  constructor(private _dateAdapter: DateAdapter<D>) {}

  /**
   * カレンダ上の日付がクリックされたら実行。
   * toDateに範囲外の日付が指定されたら、範囲の終点をセットする
   * @param date
   * @param currentRange
   */
  selectionFinished(date: D, currentRange: DateRange<D>) {
    let { start, end } = currentRange;
    // start > dateの条件を追加しているので注意
    if (start === null || start && end || start > date) {
      start = date;
      end = null;
    } else if (end === null) {
      const maxDate = this._dateAdapter.addCalendarDays(start, this.delta);
      end = date ? date > maxDate ? maxDate : date : null;
    }

    return new DateRange<D>(start, end);
  }

  /**
   * 選択される対象をプレビューで表示
   * @param activeDate
   * @param currentRange
   */
  createPreview(
    activeDate: D | null,
    currentRange: DateRange<D>
  ): DateRange<D> {
    if (currentRange.start && !currentRange.end) {
      const maxDate = this._dateAdapter.addCalendarDays(
        currentRange.start,
        this.delta
      );
      const rangeEnd = activeDate
        ? activeDate > maxDate
          ? maxDate
          : activeDate
        : null;

      return new DateRange(currentRange.start, rangeEnd);
    }

    return new DateRange<D>(null, null);
  }
}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[maxRange]',
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: MaxRangeSelectionStrategy
    }
  ]
})
export class MaxRangeDirective {
  constructor(
    @Inject(MAT_DATE_RANGE_SELECTION_STRATEGY)
    private maxRangeStrategy: MaxRangeSelectionStrategy<never>
  ) {}

  /**
   * directiveの引数として指定された値をセット
   */
  @Input() set maxRange(value: number) {
    this.maxRangeStrategy.delta = +value || 7;
  }
}
